/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { Calendar, Navigate } from 'react-big-calendar';
import dayjsLocalizer from 'react-big-calendar/lib/localizers/dayjs';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhoneIcon from '@mui/icons-material/Phone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import MDButton from 'components/MDButton';
import { GUEST_CHIP_COLOR, GUEST_CHIP_LABEL, STATUS_HEX, STATUS_LABELS, TEAL, TEAL_BORDER, formatDisplayTime } from '../constants';

const localizer = dayjsLocalizer(dayjs);
const CALENDAR_HEIGHT = 650;

// Prev on the left, label + a separate "Today" button in the middle, Next on the right.
function CalendarToolbar({ label, onNavigate }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1.5,
        px: 0.5,
      }}
    >
      <IconButton
        onClick={() => onNavigate(Navigate.PREVIOUS)}
        sx={{ border: `1px solid ${TEAL_BORDER}`, borderRadius: 2 }}
        size="small"
      >
        <ChevronLeftIcon sx={{ color: TEAL }} />
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <MDButton
          size="small"
          variant="outlined"
          color="dark"
          onClick={() => onNavigate(Navigate.TODAY)}
        >
          Today
        </MDButton>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748', minWidth: 150, textAlign: 'center' }}>
          {label}
        </Typography>
      </Box>

      <IconButton
        onClick={() => onNavigate(Navigate.NEXT)}
        sx={{ border: `1px solid ${TEAL_BORDER}`, borderRadius: 2 }}
        size="small"
      >
        <ChevronRightIcon sx={{ color: TEAL }} />
      </IconButton>
    </Box>
  );
}

// Compact colored card in the day cell; hovering reveals the full patient +
// appointment card (name, mobile, doctor, time, status) via tooltip — the
// cell itself is too small for all of that, so the tooltip carries it.
function CalendarEventCard({ event }) {
  const appt = event.resource;
  const color = STATUS_HEX[appt.status] || '#90a4ae';

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: 0.5, minWidth: 160 }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem' }}>{appt.patient_name}</Typography>
            {appt.is_guest === 'Y' && (
              <Box sx={{ px: 0.6, py: 0.1, borderRadius: 1, bgcolor: GUEST_CHIP_COLOR, color: '#fff', fontSize: '0.6rem', fontWeight: 700 }}>
                {GUEST_CHIP_LABEL}
              </Box>
            )}
          </Box>
          {appt.patient_mobile && (
            <Box display="flex" alignItems="center" gap={0.4} sx={{ mt: 0.25 }}>
              <PhoneIcon sx={{ fontSize: 12 }} />
              <Typography sx={{ fontSize: '0.7rem' }}>{appt.patient_mobile}</Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: '0.7rem', mt: 0.25 }}>{appt.doctor_name}</Typography>
          <Typography sx={{ fontSize: '0.7rem' }}>
            {formatDisplayTime(appt.appointment_time)} · Serial #{appt.serial_no}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, mt: 0.25, color: STATUS_HEX[appt.status] }}>
            {STATUS_LABELS[appt.status] || appt.status}
          </Typography>
        </Box>
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.75,
          py: 0.3,
          borderRadius: '8px',
          borderLeft: `3px solid ${color}`,
          bgcolor: `${color}1f`,
          overflow: 'hidden',
        }}
      >
        <Typography noWrap sx={{ fontSize: '0.68rem', fontWeight: 700, color }}>
          {formatDisplayTime(appt.appointment_time)} {appt.patient_name}
        </Typography>
      </Box>
    </Tooltip>
  );
}

// calendarData shape: { "YYYY-MM-DD": [{ id, appointment_no, status, patient_name, patient_mobile, doctor_name, serial_no, appointment_time }, ...] }
export default function AppointmentCalendarView({ calendarData, currentDate, onNavigate, onSelectEvent }) {
  const events = useMemo(() => {
    const result = [];
    Object.entries(calendarData || {}).forEach(([dateKey, dayAppointments]) => {
      dayAppointments.forEach(appointment => {
        result.push({
          id: appointment.id,
          title: `${formatDisplayTime(appointment.appointment_time)} ${appointment.patient_name}`.trim(),
          start: dayjs(dateKey).toDate(),
          end: dayjs(dateKey).toDate(),
          allDay: true,
          status: appointment.status,
          resource: appointment,
        });
      });
    });
    return result;
  }, [calendarData]);

  const eventPropGetter = () => ({
    style: {
      backgroundColor: 'transparent',
      border: 'none',
      padding: 0,
    },
  });

  return (
    <Box sx={{ height: CALENDAR_HEIGHT }}>
      <Calendar
        localizer={localizer}
        events={events}
        date={currentDate}
        onNavigate={onNavigate}
        views={['month']}
        defaultView="month"
        popup
        eventPropGetter={eventPropGetter}
        onSelectEvent={event => onSelectEvent(event.id)}
        components={{ toolbar: CalendarToolbar, event: CalendarEventCard }}
        style={{ height: '100%' }}
      />
    </Box>
  );
}
